import React, {Component, Suspense} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {fetchEvent} from '@checkout/actions/event';
import {loginPage} from '@checkout/actions/auth';
import {isAuthorizedAsUser} from '@checkout/selectors/auth';
import {
  setApiUrl,
  setCheckoutConfig,
  setCheckoutOverlay,
  setConfirmation,
  setListingConfig,
  setListingSlug,
  setSkipMembership,
  loadDisableMarketingPreference,
  trySelectDefaultDate,
} from '@checkout/actions/ui';
import {changedLanguage,} from '@checkout/actions/intl';
import {StyleContext} from '@checkout-c/StyleContext';
import {CurrencyContext} from '@checkout-c/CurrencyContext';
import Translations from '@checkout-h/translation';
import {TranslationContext} from '@checkout-c/Translations';
import Listing from './containers/Listing';
import {toUrlParams} from '@checkout-h/helpers';
import {
  applyTaggableCoupon,
  clearAccessCodeState,
  getHiddenPriceLevels,
  validatePresaleCoupon
} from 'checkout/views/coupon/actions/coupon';
import {getEventSeriesChildListing} from 'checkout/views/series/actions/event-series';
import {getBlockingErrors, getCmsMode, isKioskMode} from 'checkout/selectors/ui';
import {getMainEvent, isSeries} from 'checkout/selectors/event';
import Notification from './components/Notification';
import {getListingItem} from './helpers/storage';
import InteractionBlock from 'checkout/containers/InteractionBlock';
import KioskLoginContainer from './views/kiosk-management/containers/KioskLoginContainer';
import {KIOSK_AUTH_TOKEN, setKioskToken} from './views/kiosk-management/actions/kiosk-management';
import {CustomerCareHeader} from './components/CustomerCareHeader.tsx';
import MaintenanceBanner from './components/MaintenanceBanner';
import TaggableCouponBannerContainer from '../checkout/views/coupon/containers/TaggableCouponBannerContainer';
import LocalizedAddress from '../checkout/views/member/components/LocalizedAddress';
import {PaymentContextProvider} from '@showclix-shared/Payment/contexts/PaymentContext';
import EmailOptOutContainer from '../checkout/views/checkout/containers/EmailOptOutContainer';
import QuestionOptInContainer from '../checkout/views/checkout/containers/QuestionOptInContainer';
import TermsOfServiceContainer from '../checkout/views/checkout/containers/TermsOfServiceContainer';
import StepActionContainer from '../checkout/views/checkout/containers/StepActionContainer';
import InsuranceContainer from '../checkout/views/insurance/containers/InsuranceContainer';
import { PaymentPlans } from '../checkout/views/payment-plans/components/PaymentPlans';
import LoyaltyProgramContainer from '../checkout/views/loyalty-programs/containers/LoyaltyProgramContainer';
import {setAddress, setPhone} from '@checkout-v/member/actions/member';
import {HashRouter, MemoryRouter} from 'react-router';
import {loadListingDataIntoState} from './actions/general';
import {loadListingDataIntoCheckout, rehydrateDynamicListingData} from 'checkout/actions/dynamic-listing';
import NavigationService from '@checkout/components/NavigationService';
import { FocusTrapProvider } from '@checkout-b/checkout/contexts/FocusTrapContext';
import { getDynamicListingSlug } from '@checkout-b/checkout/selectors/ui';
import { getSeriesOption } from '@checkout-v/series/selectors/eventSeries';


const CheckoutContainer = React.lazy(() => import('../checkout/containers/CheckoutContainer'));

const isInitialUrl = () => {
  const hash = window.location.hash;

  if (!hash) return true;

  // '#' || '#/'
  return hash.length <= 2;
};

class App extends Component {
  stripeTerminal = null;
  t = null;

    state = {
        caughtComponentError: false,
    };

    constructor(props) {
        super(props);

        this.t = new Translations(this.props.apiUrl);
        this.t.init(
          this.props.language?.locale ?? 'en_US',
          this.props.language?.translations ?? {}
        );
    }

    componentDidCatch(error, info) {
        console.log({error, info});
        if(this.props.errorHandler) {
            this.props.errorHandler();
        }
    }

    static getDerivedStateFromError(error) {
        return { caughtComponentError: true };
    }

    createAnalyticsIframe = (listingSlug) => {
        const iframe = window.document.getElementById('analytics-container');

        if (iframe) {
            window.document.body.removeChild(iframe);
        }

        const frame = window.document.createElement('iframe');
        const originalSearchParams = new URLSearchParams(window.parent.location.search);

        originalSearchParams.forEach((_, key) => {
            if (!key.startsWith('utm_') && key !== '_gl') {
                originalSearchParams.delete(key)
            }
        });

        const searchParamsString = originalSearchParams.size ? `?${originalSearchParams.toString()}` : '';

        frame.id = 'analytics-container';
        frame.className = Math.random().toString(36).substring(7);
        frame.style.visibility = 'hidden';
        frame.style.width = '0';
        frame.style.height = '0';
        frame.style.display = 'none';
        frame.src = `/tickets/${listingSlug}/analytics` + searchParamsString;

        window.document.body.appendChild(frame);
    }

    componentDidMount() {
        // For embeds when there are search params,
        // Update search params that are not present in current url
        // and update url.
        if (window.history.pushState && this.props.searchParams) {
          const url = new URL(window.location.href);
          Object.entries(this.props.searchParams).forEach(([key, value]) => {
            if (!url.searchParams.get(key)) {
              url.searchParams.append(key, value);
            }
          })
          window?.parent?.history?.pushState(null, '', url.toString());
        }

        this.props.setSkipMembership(this.props.isSkipLogin);
        this.props.setApiUrl(this.props.apiUrl);
        this.props.setListingSlug(this.props.listingSlug);
        this.props.loadDisableMarketingPreference(this.props.listingSlug);

        if (
            this.props.dynamicListing &&
            this.props.openToListingArgs?.eventId &&
            this.props.openToListingArgs?.slug
        ) {
            // set to false because this function will set it to true, and setting it to true if
            // it is already true seems to result in weird behavior
            this.props.setCheckoutOverlay(false);
            // listing was passed in url of cart mode to open to a specific listing on mount
            this.props.loadListingDataIntoCheckout(this.props.openToListingArgs.slug, this.props.openToListingArgs.eventId);
        } else if (this.props.dynamicListing && !this.props.listingId && this.props.dynamicListingId) {
            // reload whatever listing was already dynamically loaded
            this.props.rehydrateDynamicListingData(this.props.language);
            this.createAnalyticsIframe(this.props.dynamicListingSlug);
        } else {
            this.props.loadListingDataIntoState(
                this.props.listingId,
                this.props.eventId,
                this.props.embedType,
                this.props.listingInfo,
                this.props.listingConfig,
                this.props.dynamicListing,
                this.props.maintenanceMode,
                this.props.language,
            );
        }

        this.props.setConfirmation(false);

        if (getListingItem(this.props.listingSlug, 'selectedEventId')) {
            this.props.getEventSeriesChildListing(getListingItem(this.props.listingSlug, 'selectedEventId'));
        }

        const kioskAuthToken = getListingItem(this.props.listingSlug, KIOSK_AUTH_TOKEN);
        if (kioskAuthToken) {
            this.props.setKioskToken(kioskAuthToken);
        }

        /**
         * if user previously applied an access coupon, fetch the hidden levels
         * if we end up in a state where there is an access code in local storage, but
         * no reservation, just clear the access code data from state so that user can
         * submit another one
         */
        if (this.props.coupon.accessCode !== "" && this.props.coupon.isAccessCodeValid === true && this.props.coupon.isAccessCodeApplied === true) {
            if (!!this.props.reservationToken || !!getListingItem(this.props.listingSlug, "token")) {
                this.props.getHiddenPriceLevels();
            } else {
                this.props.clearAccessCodeState();
            }
        }

        const hasSelectedEventId = () => !!getListingItem(this.props.listingSlug, "selectedEventId");

        const token = getListingItem(this.props.listingSlug, "token");

        if (isInitialUrl() && this.props.isSeries && this.props.isOpen && !hasSelectedEventId() && !token) {
            this.props.setCheckoutOverlay(false);
        }

        let reloadedPresale = false;

        if (this.props.coupon.presaleCode !== "" && this.props.coupon.isPresaleValid === true && this.props.coupon.isPresaleCodeApplied === true) {
            reloadedPresale = true;
            this.props.validatePresaleCoupon({ reload: true });
        }

        // if not fetching the reservation, handle applying taggable coupons here
        if (
            !this.props.mainEvent?.event?.isRecurring &&
            (!getListingItem(this.props.listingSlug, "token") || !getListingItem(this.props.listingSlug, "secret"))
        ) {
            this.props.applyTaggableCoupon(reloadedPresale);
        }

        if (this.props.memberDetails) {
          this.props.setPhone(this.props.memberDetails.phone);
          this.props.setAddress({
            name: this.props.memberDetails.first_name + ' ' + this.props.memberDetails.last_name,
            address: this.props.memberDetails.address,
            address2: this.props.memberDetails.address2,
            state: this.props.memberDetails.state,
            city: this.props.memberDetails.city,
            country: this.props.memberDetails.country,
            zip: this.props.memberDetails.zip
          });
        }

        if (this.props.calendarOnListing || this.props.dynamicListing) {
          this.props.trySelectDefaultDate();
        }

        const isMobile = new URLSearchParams(window.location.search).get('isMobile') === 'true';
        if (isMobile && !this.props.isAuthorized) {
            this.props.loginPage();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.dynamicListingSlug && prevProps.dynamicListingSlug !== this.props.dynamicListingSlug) {
            this.props.loadDisableMarketingPreference(this.props.dynamicListingSlug);
            this.createAnalyticsIframe(this.props.dynamicListingSlug);
        }

        const newTranslations = this.props.loadedTranslationsByServerLocale[this.props.serverLocale];
        const newTranslationsNotEmpty = (
            typeof newTranslations === 'object' &&
            newTranslations !== null &&
            Object.keys(newTranslations).length > 0
        )

        if(prevProps.serverLocale !== this.props.serverLocale || (Object.keys(this.t.mappings).length === 0 && newTranslationsNotEmpty)) {
            const newT = new Translations(this.props.apiUrl);
            newT.init(this.props.serverLocale);
            newT.resetTranslations(typeof newTranslations === 'object' && newTranslations !== null ? newTranslations : {});
            this.t = newT;
            this.props.changedLanguage();
        }

        if(prevProps.listingConfig !== this.props.listingConfig) {
            this.props.setListingConfig(this.props.listingConfig);
            this.props.setCheckoutConfig(this.props.listingConfig.checkout);
        }

        if (prevProps.isSkipLogin !== this.props.isSkipLogin) {
            this.props.setSkipMembership(this.props.isSkipLogin);
        }
    }

    /**
     * Use memory router for embed so that it doesn't impact parent frame history
     */
    getRouterType = () => {
        return this.props.isEmbed ? MemoryRouter : HashRouter;
    }

    render() {
        if (this.props.stopRenderingOnError && this.state.caughtComponentError) {
            return null;
        }

        // Use MemoryRouter in embed contexts to avoid polluting the browser's
        // session history from within the iframe. HashRouter is used in standalone mode.
        const Router = this.getRouterType();

        const params = toUrlParams(this.props.uiConfig);

        let cssUrl = "";
        if(this.props.uiConfig.siteCss) {
            cssUrl = this.props.uiConfig.siteCss[this.props.uiConfig.layout];
        }

        if (this.props.checkoutConfig && Object.keys(this.props.checkoutConfig).length > 0) {
          return (
            <TranslationContext.Provider value={this.t}>
              <PaymentContextProvider
                value={{
                  TranslationContext,
                  LocalizedAddress,
                  LoyaltyProgram: <LoyaltyProgramContainer />,
                  Insurance: <InsuranceContainer />,
                  PaymentPlans: <PaymentPlans />,
                  OptInsOuts: (
                    <>
                      <TermsOfServiceContainer />
                      <EmailOptOutContainer />
                      <QuestionOptInContainer />
                    </>
                  ),
                  StepAction: StepActionContainer,
                }}
              >
                <StyleContext.Provider value={this.props.styleVars}>
                  <CurrencyContext.Provider value={this.props.currencyConfig}>
                    <FocusTrapProvider>
                      <Router>
                        <NavigationService />
                      {this.props.uiConfig.layout && !this.props.uiConfig.skipListingCss && (
                        <link rel="stylesheet" href={cssUrl} />
                      )}
                      <div id="mangement-access-div"></div>
                      <div id="kiosk__management"></div>
                      <div id="help"></div>
                      <MaintenanceBanner maintenanceMode={this.props.maintenanceMode} />
                      {this.props.blockingErrors.length > 0 && <Notification messages={this.props.blockingErrors} />}
                      <div className="bottom-message-banner">
                        <TaggableCouponBannerContainer />
                      </div>
                      <div id="timeout-overlay"></div>
                      <ConfiguredListing config={this.props.checkoutConfig.listing} />
                      {this.props.maintenanceMode === false && (
                        <Suspense fallback={null}>
                            <CheckoutContainer
                              router={this.props.router}
                              store={this.props.store}
                              apiUrl={this.props.apiUrl}
                              listingConfig={this.props.listingConfig}
                            />
                        </Suspense>
                      )}
                      </Router>
                    </FocusTrapProvider>
                  </CurrencyContext.Provider>
                </StyleContext.Provider>
              </PaymentContextProvider>
            </TranslationContext.Provider>
          );
        }

        return (
          <TranslationContext.Provider value={this.t}>
            <PaymentContextProvider
              value={{
                TranslationContext,
                LocalizedAddress,
                StepAction: StepActionContainer,
                LoyaltyProgram: <LoyaltyProgramContainer />,
                Insurance: <InsuranceContainer />,
                PaymentPlans: <PaymentPlans />,
                OptInsOuts: (
                  <>
                    <TermsOfServiceContainer />
                    <EmailOptOutContainer />
                    <QuestionOptInContainer />
                  </>
                ),
              }}
            >
              <StyleContext.Provider value={this.props.styleVars}>
                <CurrencyContext.Provider value={this.props.currencyConfig}>
                  <FocusTrapProvider>
                    {this.props.uiConfig.layout && !this.props.uiConfig.skipListingCss && (
                      <link rel="stylesheet" href={cssUrl} />
                    )}
                    {this.renderListing()}
                  </FocusTrapProvider>
                </CurrencyContext.Provider>
              </StyleContext.Provider>
            </PaymentContextProvider>
          </TranslationContext.Provider>
        );
    }

  renderListing = () => {

    const Router = this.getRouterType();

    // maybe these named divs should be portaled
    return (
      <Router>
        <div id="mangement-access-div"></div>
        <div id="kiosk__management"></div>
        <MaintenanceBanner maintenanceMode={this.props.maintenanceMode} />
        <InteractionBlock />
        {this.props.blockingErrors.length > 0 && <Notification messages={this.props.blockingErrors} />}
        <div id="kiosk__time__container"></div>
        <div className="bottom-message-banner">
          <TaggableCouponBannerContainer />
          {!this.props.dynamicListing && (
            <CustomerCareHeader {...(this.props.uiConfig.customerCare || {})} />
          )}
        </div>
        {this.props.isKiosk && !this.props.kioskToken && !this.props.cmsMode ? (
          <TranslationContext.Provider value={this.t}>
            <KioskLoginContainer />
          </TranslationContext.Provider>
        ) : (
          <Listing />
        )}
          <NavigationService />
          {this.props.maintenanceMode === false && (
            <CheckoutContainer
              store={this.props.store}
              apiUrl={this.props.apiUrl}
              listingConfig={this.props.listingConfig}
            />
          )}
      </Router>
    );
  };
}

const mapStateToProps = (state, props) => {
  const hasReservation = getListingItem(props.listingSlug, 'token');
  const isSkipLogin = !!(state.ui?.uiConfig?.membershipFormConfig?.skip_login);
  const isEmbed = state.ui.isEmbed;
  const skip = hasReservation || (isSkipLogin && !isEmbed);

  return {
        styleVars: state.ui.styleVars,
        uiConfig: state.ui.uiConfig,
        isOpen: state.ui.overlay,
        serverLocale: state.intl.serverLocale,
        locale: state.intl.locale,
        maintenanceMode: state.ui.maintenanceMode,
        currencyConfig: state.ui.currencyConfig,
        coupon: state.coupon,
        blockingErrors: state.kioskManagement.token ? getBlockingErrors(state) : [],
        sellerId: state.event.seller_id,
        isKiosk: isKioskMode(state),
        kioskToken: state.kioskManagement.token,
        cmsMode: getCmsMode(state),
        reservationToken: state.reservation.token,
        mainEvent: getMainEvent(state),
        loadedTranslationsByServerLocale: state.intl.loadedTranslationsByServerLocale,
        changedLanguageCount: state.intl.changedLanguageCount, // just to force a ui refresh
        embedType: state.ui.embedType,
        isSeries: isSeries(state),
        dynamicListingId: state.dynamicListing.listingId,
        isSkipLogin: skip,
        dynamicListingSlug: getDynamicListingSlug(state),
        calendarOnListing: !!getSeriesOption("calendarOnListing", false)(state),
        isEmbed,
        isAuthorized: !!isAuthorizedAsUser(state),
    };
}

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        fetchEvent,
        setConfirmation,
        setApiUrl,
        setListingSlug,
        loadDisableMarketingPreference,
        setListingConfig,
        validatePresaleCoupon,
        getEventSeriesChildListing,
        getHiddenPriceLevels,
        setCheckoutConfig,
        setCheckoutOverlay,
        setKioskToken,
        setSkipMembership,
        clearAccessCodeState,
        applyTaggableCoupon,
        changedLanguage,
        setPhone,
        setAddress,
        loadListingDataIntoState,
        rehydrateDynamicListingData,
        loadListingDataIntoCheckout,
        trySelectDefaultDate,
        loginPage,
    }, dispatch);
}

App.defaultProps = {
  maintenanceMode: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
